import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router";
import { useComponentTranslation } from "@/services/i18n";
import { FaCopy, FaGoogle, FaArrowLeft } from "react-icons/fa";
import toast from "react-hot-toast";
import { CANDIDATE_URL } from "@/config";
import API from "@/services/api";
import PublishToggle from "./components/publish-toggle";

const translations = {
  en: {
    title: "Google for Jobs",
    description: "Your job is automatically indexed by Google for Jobs when published",
    status: "Indexing Status",
    active: "Active",
    inactive: "Inactive",
    copy_link: "Copy Link",
    link_copied: "Link copied to clipboard",
    learn_more: "Learn More",
    info: "Your job posting is automatically included in Google for Jobs search results. No additional action required.",
    publish: "Publish",
    unpublish: "Unpublish",
    post_published: "Post published successfully",
    post_unpublished: "Post unpublished successfully",
    post_not_enabled: "Failed to publish post",
    back: "Back to channels",
  },
  fr: {
    title: "Google for Jobs",
    description: "Votre offre est automatiquement indexée par Google for Jobs lors de la publication",
    status: "Statut d'indexation",
    active: "Actif",
    inactive: "Inactif",
    copy_link: "Copier le lien",
    link_copied: "Lien copié dans le presse-papiers",
    learn_more: "En savoir plus",
    info: "Votre offre d'emploi est automatiquement incluse dans les résultats de recherche Google for Jobs. Aucune action supplémentaire requise.",
    publish: "Publier",
    unpublish: "Dépublier",
    post_published: "Publication publiée avec succès",
    post_unpublished: "Publication dépubliée avec succès",
    post_not_enabled: "Échec de la publication",
    back: "Retour aux canaux",
  },
};

export default function GoogleForJobs({ job, setJob, posts = [], setPosts }) {
  const { t } = useComponentTranslation(translations);
  const { id: jobId } = useParams();
  const navigate = useNavigate();
  const [channel, setChannel] = useState(null);
  const channelSlug = "google-for-jobs";

  useEffect(() => {
    fetchChannel();
  }, []);

  const fetchChannel = async () => {
    try {
      const response = await API.post(`/channel/search`, {
        slug: channelSlug,
        limit: 1,
      });
      if (response.ok && response.data?.[0]) {
        setChannel(response.data[0]);
      }
    } catch (e) {
      console.error("Error fetching channel:", error);
    }
  };

  // Find job post from posts array
  const jobPost = channel ? posts.find((post) => post.channel_id === channel._id) : null;

  const shareLink = useMemo(() => {
    if (!job?.organization_slug || !jobPost?._id) return "";
    return `${CANDIDATE_URL}/companies/${job.organization_slug}/job/${jobPost._id}`;
  }, [job?.organization_slug, jobPost?._id]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareLink);
    toast.success(t("link_copied"));
  };

  const handleLearnMore = () => {
    window.open("https://developers.google.com/search/docs/appearance/structured-data/job-posting", "_blank");
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Back button */}
      <button onClick={() => navigate("..")} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4">
        <FaArrowLeft className="w-4 h-4" />
        {t("back")}
      </button>

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 via-red-500 to-yellow-500 rounded-lg flex items-center justify-center shrink-0">
            <FaGoogle className="w-8 h-8 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-2xl font-bold text-gray-900">{t("title")}</h3>
              {channel && <PublishToggle job={job} channel={channel} posts={posts} setPosts={setPosts} />}
            </div>

            <p className="text-sm text-gray-600 mb-6">{t("description")}</p>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-700">{t("info")}</p>
            </div>

            <div className="flex items-center gap-2">
              <button onClick={handleCopyLink} className="btn btn-secondary flex items-center gap-2">
                <FaCopy className="w-4 h-4" />
                {t("copy_link")}
              </button>
              <button onClick={handleLearnMore} className="btn btn-secondary">
                {t("learn_more")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
